import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VISIBLE_TOTAL } from '../albumData';
import { C } from '../albumColors';

/**
 * Panel expandido con TODA la lista de coleccionistas. Se monta en el
 * sidebar derecho cuando expandedView === 'collectors'. A diferencia del
 * UserSearchCard (que muestra top 5), acá mostramos todos con búsqueda y
 * filtro por comunidad. El album baja de 5 a 4 columnas para ceder un poco
 * de espacio (no la mitad de la pantalla como hace el dashboard).
 */
function AlbumCollectorsPanel({ allUsers, onViewUser, myCommunities }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [communityFilter, setCommunityFilter] = useState(null); // community id or null = all
  const [sortBy, setSortBy] = useState('owned'); // 'owned' | 'name'

  const communityNameById = useMemo(() => {
    const m = new Map();
    for (const c of myCommunities || []) m.set(c.id, c.name);
    return m;
  }, [myCommunities]);

  const communitiesLabel = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return null;
    const names = ids.map(id => communityNameById.get(id)).filter(Boolean);
    if (names.length === 0) return null;
    if (names.length <= 2) return names.join(' · ');
    return `${names[0]} · +${names.length - 1}`;
  };

  const filtered = useMemo(() => {
    const base = (allUsers || []).filter(u => {
      if (communityFilter != null) {
        const ids = u.communityIds || [];
        if (!ids.includes(communityFilter)) return false;
      }
      if (query.trim()) {
        return u.name.toLowerCase().includes(query.toLowerCase());
      }
      return true;
    });
    if (sortBy === 'name') {
      return base.sort((a, b) => a.name.localeCompare(b.name));
    }
    return base.sort((a, b) => (b.owned ?? 0) - (a.owned ?? 0));
  }, [allUsers, query, communityFilter, sortBy]);

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 pt-4 pb-3 border-b border-outline-variant shrink-0 bg-surface space-y-3">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-outline">
            <span className="material-symbols-outlined text-base">search</span>
          </div>
          <input
            className="w-full border-none rounded-lg pl-10 pr-3 py-2.5 text-sm bg-surface-container-highest text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            placeholder={t('Buscar por nombre...')}
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            <button
              type="button"
              onClick={() => setCommunityFilter(null)}
              className={`px-3 py-1 rounded-full text-[11px] font-bold transition-colors ${
                communityFilter == null
                  ? 'bg-primary text-on-primary'
                  : 'bg-surface-container-highest text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              {t('Todas')}
            </button>
            {(myCommunities || []).map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCommunityFilter(c.id)}
                className={`px-3 py-1 rounded-full text-[11px] font-bold transition-colors max-w-[120px] truncate ${
                  communityFilter === c.id
                    ? 'bg-primary text-on-primary'
                    : 'bg-surface-container-highest text-on-surface-variant hover:bg-surface-container-high'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 text-[10px] font-bold text-on-surface-variant">
            <span className="uppercase tracking-widest">{t('Orden')}</span>
            <button
              type="button"
              onClick={() => setSortBy(s => s === 'owned' ? 'name' : 'owned')}
              className="px-2 py-1 rounded-md bg-surface-container-highest hover:bg-surface-container-high inline-flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-sm">
                {sortBy === 'owned' ? 'leaderboard' : 'sort_by_alpha'}
              </span>
              {sortBy === 'owned' ? t('Progreso') : t('Nombre')}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-on-surface-variant pt-1">
          <span>{t('{{count}} coleccionistas', { count: filtered.length })}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto hide-scrollbar p-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-12 text-on-surface-variant">
            <span className="material-symbols-outlined text-4xl mb-2 opacity-60">person_search</span>
            <p className="text-sm font-bold">{t('Sin coleccionistas')}</p>
            <p className="text-xs mt-1">{t('Probá cambiar el filtro o el término de búsqueda.')}</p>
          </div>
        ) : (
          // auto-fit con minmax garantiza que el grid pase de 2 a 1 columna
          // cuando el ancho del CONTENEDOR (no del viewport) baja por debajo
          // de 240px × 2 + gap, así el reflow respeta el ancho real del
          // panel mientras el sidebar se achica con la pantalla. 240px es
          // el mínimo para que cada card (avatar 40 + content + chevron) se
          // lea cómoda con la barra de progreso completa.
          <div className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
            {filtered.map(user => {
              const owned = user.owned ?? 0;
              const pct = Math.round((owned / VISIBLE_TOTAL) * 100);
              const labels = communitiesLabel(user.communityIds);
              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => onViewUser(user)}
                  className="group flex items-center gap-3 p-2.5 rounded-xl bg-surface hover:bg-surface-container border border-outline-variant/40 hover:border-primary/40 transition-all text-left"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                    style={{ backgroundColor: user.color || C.primary }}
                  >
                    {user.initial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate text-on-surface">{user.name}</p>
                    {labels && (
                      <p className="text-[10px] truncate text-on-surface-variant">{labels}</p>
                    )}
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1 h-1 bg-surface-container-highest rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: user.color || C.primary }}
                        />
                      </div>
                      <span className="text-[10px] font-bold text-on-surface-variant tabular-nums">{pct}%</span>
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-base text-outline group-hover:text-primary transition-colors">chevron_right</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AlbumCollectorsPanel;
